import { useDisclosure } from "@mantine/hooks";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { Button } from "src/components/shared/Button/Button";
import { type IReportEntry } from "src/pages/Reports/Report/Report.types";
import { useSelector } from "src/redux/hooks";
import { type ILogbookCharts } from "src/redux/slices/entriesGeneratorSlice";
import { getObjectKeys } from "src/utils";

import {
  extractCellValue,
  findLeafColumn,
  findLeafColumnIndex,
  getCellAtLeafIndex,
  getNormalizedRows,
  getRowCols,
  getWorkingHeaders,
  mergeChartSettings,
} from "./CreateChartButton.utils";
import CreateChartModal from "./CreateChartModal";

interface Props {
  entries: IReportEntry[];
}

const DRAFT_STATUS = 0;
const UNVERIFIED_STATUSES = new Set([1, 2]); // Open, Re-Verify

const CreateChartButton = ({ entries }: Props) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [data, setData] = useState<ILogbookCharts[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeUnverified, setIncludeUnverified] = useState(true);

  const { selectedCols, sections, chartsData } = useSelector(
    state => state.entriesGeneratorSlice,
  );

  const buildChartData = useCallback((withUnverified: boolean): ILogbookCharts[] => {
    const chartsDataObj: Record<
      string,
      Record<
        string,
        {
          name: string;
          sectionIndex: number;
          dataType?: "categorical" | "numeric" | "date";
          vals: Array<string | number>;
          rawVals: Array<string | number | undefined>;
          dates: string[];
        }
      >
    > = {};

    getObjectKeys(selectedCols).forEach((lbid: string) => {
      const logbookEntries = entries
        .filter(
          entry =>
            entry.lbid === lbid &&
            Number(entry.deleted) !== 1 &&
            Number(entry.status) !== DRAFT_STATUS &&
            (withUnverified || !UNVERIFIED_STATUSES.has(Number(entry.status))),
        )
        .sort(
          (a, b) => dayjs(a.datecreated).valueOf() - dayjs(b.datecreated).valueOf(),
        );

      getObjectKeys(selectedCols[lbid]).forEach((sIndex: number) => {
        const sectionIndex = Number(sIndex);
        const colNames = selectedCols[lbid][sectionIndex];
        if (!colNames?.length) return;

        const sectionTemplate = sections[lbid]?.[sectionIndex];
        if (!sectionTemplate || sectionTemplate.subSections) return;
        const headers = getWorkingHeaders(sectionTemplate.headers);

        const colLeafIndices = colNames
          .map(colName => ({
            colName,
            leafIndex: findLeafColumnIndex(headers, colName),
            dataType: findLeafColumn(headers, colName)?.dataType,
          }))
          .filter(({ leafIndex }) => leafIndex !== -1);
        if (!colLeafIndices.length) return;

        logbookEntries.forEach(entry => {
          const section = entry.lbentry.sections?.[sectionIndex];
          if (!section || section.subSections) return;

          // Normalize rows/cols once per entry instead of once per
          // selected column.
          getNormalizedRows(section.rows).forEach(row => {
            const rowCols = getRowCols(row);

            colLeafIndices.forEach(({ colName, leafIndex, dataType }) => {
              const cell = getCellAtLeafIndex(rowCols, leafIndex);
              const value = extractCellValue(cell);
              const key = `${sectionIndex}::${colName}`;

              if (!chartsDataObj[lbid]) chartsDataObj[lbid] = {};
              if (!chartsDataObj[lbid][key])
                chartsDataObj[lbid][key] = {
                  name: colName,
                  sectionIndex,
                  dataType,
                  vals: [],
                  rawVals: [],
                  dates: [],
                };

              chartsDataObj[lbid][key].rawVals.push(value);
              if (value === undefined) return;

              chartsDataObj[lbid][key].vals.push(value);
              chartsDataObj[lbid][key].dates.push(entry.datecreated);
            });
          });
        });
      });
    });

    return Object.entries(chartsDataObj)
      .map(([lbid, colsData]) => ({
        lbid,
        data: getObjectKeys(colsData)
          .filter(key => colsData[key].vals.length > 0)
          .map(key => ({
            id: crypto.randomUUID(),
            name: colsData[key].name,
            sectionIndex: colsData[key].sectionIndex,
            dataType: colsData[key].dataType,
            type: "line" as const,
            vals: colsData[key].vals,
            rawVals: colsData[key].rawVals,
            dates: colsData[key].dates,
            trendline: false,
          })),
      }))
      .filter(({ data }) => data.length > 0);
  }, [entries, sections, selectedCols]);

  const handleCreateChart = useCallback(() => {
    // Defer the heavy traversal to the next tick so the button can paint
    // its loading state first, instead of the click freezing with no
    // visual feedback while the computation runs.
    setLoading(true);
    setTimeout(() => {
      setData(mergeChartSettings(buildChartData(includeUnverified), chartsData));
      setLoading(false);
      open();
    }, 0);
  }, [buildChartData, chartsData, includeUnverified, open]);

  const handleIncludeUnverifiedChange = useCallback(
    (checked: boolean) => {
      setIncludeUnverified(checked);
      setData(prev => mergeChartSettings(buildChartData(checked), prev));
    },
    [buildChartData],
  );

  const disabled = useMemo(
    () =>
      !Object.values(selectedCols).some(log =>
        Object.values(log).some(sec => sec.length > 0),
      ),
    [selectedCols],
  );

  return (
    <>
      <Button disabled={disabled} loading={loading} onClick={handleCreateChart}>
        {chartsData.length > 0 ? "Edit" : "Create"}
      </Button>
      <CreateChartModal
        opened={opened}
        close={close}
        data={data}
        setData={setData}
        includeUnverified={includeUnverified}
        onIncludeUnverifiedChange={handleIncludeUnverifiedChange}
      />
    </>
  );
};

export default CreateChartButton;
